import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { roofTypes } from "@/components/configurator/productData";
import { Home, Building, Building2, Warehouse, Calculator, ArrowRight, Zap, Euro, CheckCircle, Clock, Target } from "lucide-react";
import { track } from "@/lib/analytics";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

const InstantQuoteSection = () => {
  const [step, setStep] = useState(1);
  // Load saved form data from localStorage
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('instant_quote_form_data');
    return saved ? JSON.parse(saved) : {
      postcode: "",
      energyUsage: "",
      gasUsage: "",
      houseType: "",
      roofType: "",
      paymentPreference: ""
    };
  });
  const [result, setResult] = useState<any>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const getRoofIcon = (roofId: string) => {
    switch (roofId) {
      case 'tile-roof': return Home;
      case 'flat-roof': return Building2;
      case 'metal-roof': return Warehouse;
      case 'slate-roof': return Building;
      default: return Home;
    }
  };

  const calculateOptimalPackage = (energy: number, gas: number, roofType: string, houseType: string) => {
    // Smart package recommendation algorithm
    let recommendedPanels;
    if (energy <= 2500) recommendedPanels = 6;
    else if (energy <= 3500) recommendedPanels = 8;
    else if (energy <= 4500) recommendedPanels = 12;
    else if (energy <= 6000) recommendedPanels = 16;
    else recommendedPanels = 20;
    
    // Roof type adjustments
    if (roofType === 'flat-roof') recommendedPanels += 2; // More space available
    if (roofType === 'slate-roof') recommendedPanels -= 2; // Limited mounting options
    
    // House type adjustments
    if (houseType === 'appartement') recommendedPanels = Math.min(recommendedPanels, 8);
    if (houseType === 'vrijstaand') recommendedPanels += 2; // More roof space
    
    return Math.max(6, Math.min(24, recommendedPanels));
  };

  const calculateQuote = () => {
    const energy = parseInt(formData.energyUsage);
    const gas = parseInt(formData.gasUsage);
    
    // Get smart recommendation
    const recommendedPanels = calculateOptimalPackage(energy, gas, formData.roofType, formData.houseType);
    
    // New pricing structure: €200 deposit + €200 financing per panel
    const depositPerPanel = 200;
    const totalValuePerPanel = 400;
    
    const deposit = recommendedPanels * depositPerPanel;
    const totalSystemValue = recommendedPanels * totalValuePerPanel;
    const financingAmount = totalSystemValue - deposit;
    
    // Calculate energy production and savings
    const baseProduction = recommendedPanels * 400; // 400W per panel
    const annualProduction = Math.round(baseProduction * 0.88); // 88% efficiency
    
    const energyRate = 0.30; // €0.30 per kWh
    const gasRate = 1.25; // €1.25 per m³
    
    const energySavings = Math.min(energy, annualProduction) * energyRate;
    const gasSavings = Math.min(gas * 0.1, 300) * gasRate;
    const totalAnnualSavings = Math.round(energySavings + gasSavings);
    const monthlySavings = Math.round(totalAnnualSavings / 12);
    
    // Calculate payback period for financing part
    const paybackMonths = Math.ceil(financingAmount / monthlySavings);
    const monthlyPayment = Math.round(financingAmount / paybackMonths);
    
    setResult({
      recommendedPanels,
      annualProduction,
      monthlySavings,
      totalAnnualSavings,
      deposit,
      totalSystemValue,
      financingAmount,
      paybackMonths,
      monthlyPayment,
      roofSuitability: "Zeer geschikt"
    });
    
    // Save to localStorage for OfferteSnell page
    localStorage.setItem('calculatorData', JSON.stringify({
      postcode: formData.postcode,
      jaarverbruik: parseInt(formData.energyUsage),
      gasverbruik: parseInt(formData.gasUsage),
      huisType: formData.houseType,
      dakOrientatie: formData.roofType,
      aanbevolenPanelen: recommendedPanels,
      jaarProduktie: annualProduction,
      maandBesparing: monthlySavings,
      jaarBesparing: totalAnnualSavings,
      pakketPrijs: deposit,
      terugverdientijd: Math.round(paybackMonths / 12),
      afbetalingVoorkeur: formData.paymentPreference,
      maandAflossing: monthlyPayment,
      calculatedAt: new Date().toISOString(),
      source: 'homepage_calculator'
    }));

    track("instant_quote_calculated", {
      recommendedPanels,
      deposit,
      totalAnnualSavings,
      paybackMonths
    });
  };

  const handleNext = () => {
    // Save current form data to localStorage for persistence
    localStorage.setItem('instant_quote_form_data', JSON.stringify(formData));
    
    if (step < 5) {
      setStep(step + 1);
      track(`instant_quote_step_${step + 1}`);
    } else {
      calculateQuote();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      track(`instant_quote_step_back_${step - 1}`);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1: return formData.postcode.length >= 4;
      case 2: return formData.energyUsage !== "";
      case 3: return formData.gasUsage !== "";
      case 4: return formData.houseType !== "" && formData.roofType !== "";
      case 5: return formData.paymentPreference !== "";
      default: return false;
    }
  };

  if (result) {
    return (
      <section id="instant-quote" className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-6 md:p-8 shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Uw Persoonlijke Zonneplan
              </h2>
                <p className="text-muted-foreground">
                Berekeningsresultaat voor postcode {formData.postcode}
                </p>
                <p className="text-xs text-orange-600 mt-2 italic">
                * Besparingen zijn een schatting - exacte waarden worden berekend in de officiële offerte
                </p>
            </div>

            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-6 rounded-lg space-y-6">
              {/* Investment Section */}
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-primary">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                  <Euro className="h-5 w-5" />
                  Uw Investering
                </h3>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">€{result.deposit.toLocaleString()}</div>
                  <p className="text-muted-foreground">Eenmalige aanbetaling (materiaal + levering)</p>
                  <div className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Spullen direct op pallet bij u geleverd - Geen risico!
                  </div>
                </div>
              </div>

              {/* Monthly Benefits */}
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-green-600 mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Maandelijks Voordeel
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">€{result.monthlySavings}</div>
                    <p className="text-sm text-muted-foreground">Energiebesparing per maand</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">€{result.monthlyPayment}</div>
                    <p className="text-sm text-muted-foreground">Hiervan voor systeem aflossing</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-600 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Terugverdientijd
                </h3>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{result.paybackMonths} maanden</div>
                  <p className="text-muted-foreground">Daarna volledig eigen geld besparen!</p>
                  <div className="text-sm text-green-600 mt-2">€{result.monthlySavings}/maand extra in uw portemonnee</div>
                </div>
              </div>

              {/* System Details */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Uw Zonnesysteem Aanbeveling
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-xl font-bold text-primary">{result.recommendedPanels}</div>
                    <div className="text-muted-foreground">Aanbevolen panelen</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-xl font-bold text-green-600">{result.annualProduction} kWh</div>
                    <div className="text-muted-foreground">Jaarlijkse productie</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-xl font-bold text-blue-600">€{result.totalSystemValue.toLocaleString()}</div>
                    <div className="text-muted-foreground">Totale systeemwaarde</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button
                size={isMobile ? "default" : "lg"}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => {
                  track("instant_quote_direct_quote_request");
                  navigate("/offerte-snel");
                }}
              >
                Direct Offerte Aanvragen
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size={isMobile ? "default" : "lg"}
                onClick={() => {
                  track("instant_quote_request_official");
                  (window as any).Calendly?.initPopupWidget?.({
                    url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                    pageSettings: {
                      backgroundColor: 'ffffff',
                      hideEventTypeDetails: false,
                      hideLandingPageDetails: false,
                      primaryColor: 'ff7800',
                      textColor: '4d5055'
                    }
                  });
                }}
              >
                Plan Gesprek In
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size={isMobile ? "default" : "lg"}
                onClick={() => {
                  setStep(1);
                  setResult(null);
                  setFormData({ 
                    postcode: "", 
                    energyUsage: "", 
                    gasUsage: "",
                    roofType: "", 
                    houseType: "",
                    paymentPreference: ""
                  });
                  track("instant_quote_restart");
                }}
              >
                Nieuwe Berekening
              </Button>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="instant-quote" className="py-12 md:py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <Card className="max-w-2xl mx-auto p-6 md:p-8 shadow-lg">
          <div className="text-center mb-8">
            <Calculator className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Bereken Uw Besparing in 30 Seconden
            </h2>
            <p className="text-muted-foreground">
              Krijg direct inzicht in uw mogelijke besparing en terugverdientijd
            </p>
          </div>

          {/* Enhanced Progress indicator */}
          <div className="text-center mb-6">
            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      i <= step 
                        ? "bg-primary border-primary text-primary-foreground" 
                        : i === step + 1
                        ? "border-primary/50 text-primary/50"
                        : "border-muted-foreground/30 text-muted-foreground/30"
                    }`}
                  >
                    <span className="text-xs font-semibold">{i}</span>
                  </div>
                  {i < 5 && (
                    <div 
                      className={`w-12 h-0.5 mt-1 transition-all duration-300 ${
                        i < step ? "bg-primary" : "bg-muted-foreground/30"
                      }`} 
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="bg-muted/50 rounded-lg px-4 py-2 inline-block">
              <p className="text-sm font-medium text-foreground">
                Stap {step} van 5: {
                  step === 1 ? "Locatie" :
                  step === 2 ? "Stroomverbruik" :
                  step === 3 ? "Gasverbruik" :
                  step === 4 ? "Woningtype" :
                  "Financiering"
                }
              </p>
              <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                <div 
                  className="bg-primary h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${(step / 5) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Wat is uw postcode?
                </label>
                <Input
                  placeholder="Bijv. 6661"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  className="text-center text-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We gebruiken dit voor een nauwkeurige zonnestraling berekening
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Wat is uw jaarlijkse stroomverbruik?
                </label>
                <Select onValueChange={(value) => setFormData({ ...formData, energyUsage: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer uw verbruik" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2000">Tot 2.000 kWh (1-2 personen)</SelectItem>
                    <SelectItem value="3500">2.000 - 4.000 kWh (2-3 personen)</SelectItem>
                    <SelectItem value="5000">4.000 - 6.000 kWh (3-4 personen)</SelectItem>
                    <SelectItem value="7000">6.000+ kWh (4+ personen)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Te vinden op uw jaarrekening van de energieleverancier
                </p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Wat is uw jaarlijkse gasverbruik?
                </label>
                <Select onValueChange={(value) => setFormData({ ...formData, gasUsage: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer uw gasverbruik" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="800">Tot 800 m³ (1-2 personen)</SelectItem>
                    <SelectItem value="1200">800 - 1.500 m³ (2-3 personen)</SelectItem>
                    <SelectItem value="1800">1.500 - 2.200 m³ (3-4 personen)</SelectItem>
                    <SelectItem value="2500">2.200+ m³ (4+ personen)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Gemiddeld Nederlands huishouden: 1.200 m³ per jaar
                </p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Woningtype en daktype</h3>
              
              {/* House Type Selection */}
              <div>
                <label className="text-sm font-medium mb-3 block">Type woning</label>
                <Select value={formData.houseType} onValueChange={(value) => setFormData({...formData, houseType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer woningtype" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rijtjeshuis">Rijtjeshuis</SelectItem>
                    <SelectItem value="tussenboerderij">Tussenboerderij</SelectItem>
                    <SelectItem value="hoekwoning">Hoekwoning</SelectItem>
                    <SelectItem value="2-onder-1-kap">2-onder-1-kap</SelectItem>
                    <SelectItem value="vrijstaand">Vrijstaand</SelectItem>
                    <SelectItem value="appartement">Appartement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Roof Type Visual Selection */}
              <div>
                <label className="text-sm font-medium mb-3 block">Type dak</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {roofTypes.map((roof) => {
                    const IconComponent = getRoofIcon(roof.id);
                    const isSelected = formData.roofType === roof.id;
                    return (
                      <Card 
                        key={roof.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          isSelected ? 'ring-2 ring-primary border-primary bg-primary/5' : 'hover:border-primary/50'
                        }`}
                        onClick={() => setFormData({...formData, roofType: roof.id})}
                      >
                        <CardContent className="p-4 text-center">
                          <IconComponent className={`h-8 w-8 mx-auto mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          <h4 className={`font-medium text-sm ${isSelected ? 'text-primary' : ''}`}>{roof.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{roof.description}</p>
                          {isSelected && (
                            <div className="mt-2 text-xs font-medium text-primary">✓ Geselecteerd</div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Hoe wilt u de investering aanpakken?
                </label>
                <Select onValueChange={(value) => setFormData({ ...formData, paymentPreference: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies uw voorkeur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aanbetaling_besparing">Aanbetaling + afbetaling via besparing (aanbevolen)</SelectItem>
                    <SelectItem value="50_50_betaling">50% vooraf, 50% na oplevering installatie</SelectItem>
                    <SelectItem value="financiering">Financiering via bank</SelectItem>
                    <SelectItem value="lease">Lease constructie</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Bij aanbetaling + besparing betaalt u alleen materiaalkosten vooraf, de rest via uw maandelijkse energiebesparing
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1"
                size={isMobile ? "default" : "lg"}
              >
                Vorige
              </Button>
            )}
            <Button
              disabled={!isStepValid()}
              onClick={handleNext}
              className="flex-1 bg-primary hover:bg-primary/90"
              size={isMobile ? "default" : "lg"}
            >
              {step === 5 ? "Bereken Besparing" : "Volgende Stap"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default InstantQuoteSection;